// Authoritative cost-table formula for one airline+transport option. This is
// the SAME math the display path renders (FinalQuoteCostTable), extracted here
// as a pure, tested function so the display, the finalize path, and any future
// server-side check share ONE definition instead of drifting copies.
//
// Line items:
//   freight/warRisk/handling — per-kg rate × CW, floored at the line min-charge
//   + customs (spCF), then × (1 + fuel%/100)
//   + transportCost (agent/hauler)
//   + a flat screening amount (FSc or TSc) when applicable
//   = baseCost
//   + margin% of baseCost + a flat extra = grandTotal
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * What one screening choice actually costs.
 *
 * Every screening rate in this system is PER-KG with a minimum charge — the
 * airline ones (PAS/SAS) and the transport ones (PTS/STS) alike. The render
 * path used to treat transport screening as a flat figure and compute
 * `Math.max(ratePerKg, minCharge)`, comparing £0.18/kg against a £22 minimum
 * and charging £22, while the chip the operator clicks showed £103.68
 * (0.18 x 576). The row and the button disagreed about the same screening, and
 * that is one of the three totals the operator found for one shipment.
 *
 * `flat` is honoured when a rate was captured as an absolute amount.
 */
export function resolveScreening({ ratePerKg, flat, minCharge, CW } = {}) {
  const min = num(minCharge);
  if (flat !== undefined && flat !== null && flat !== "") {
    const amount = Math.max(num(flat), min);
    return {
      amount,
      basis: "flat",
      rate: num(flat),
      minApplied: min > num(flat),
      minCharge: min,
    };
  }
  const rate = num(ratePerKg);
  const perKgTotal = rate * num(CW);
  const amount = Math.max(perKgTotal, min);
  return {
    amount,
    basis: "per-kg",
    rate,
    minApplied: min > perKgTotal,
    minCharge: min,
  };
}

/**
 * Can this option be priced at all?
 *
 * The air rate is the only genuinely required input. Haulage is NOT: an
 * airport-to-airport shipment has no collection leg, and the table previously
 * aborted on `!option.transportCost`, which treats a legitimate £0 collection
 * identically to a missing one. The effect was that airport-to-airport could
 * not be quoted in FreightKit at all — the row silently produced no total.
 */
export function isOptionPriceable(option) {
  if (!option) return false;
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  /* Scoped to THIS table, whose every row is an airline x hauler combination.
     An option here with no air rate is a data error, not a cheap quote, and
     rendering £0.00 of freight would quote a customer a price for a flight
     nobody priced.

     Options that genuinely carry no headline rate — the quote sheet's, which
     price in named charge lines (THC, AWB, DOCS, COLL…) — are not forced
     through here at all. They have their own table, QuoteSheetCostTable, so
     this gate does not have to stretch to cover two different things. */
  return n(option.airline?.ratePerVolumeWeight) > 0;
}

export function computeOptionGrandTotal(p = {}) {
  const CW = num(p.CW);
  const FR = num(p.FR);
  const WR = num(p.WR);
  const HF = num(p.HF);
  const FS = num(p.FS);

  const line = (label, rate, minCharge) => {
    const perKgTotal = rate * CW;
    const min = num(minCharge);
    return {
      label,
      basis: "per-kg",
      rate,
      qty: CW,
      amount: Math.max(perKgTotal, min),
      minApplied: min > perKgTotal,
      minCharge: min,
    };
  };

  const freight = line("Freight", FR, p.freightMin);
  const warRisk = line("War risk", WR, p.warRiskMin);
  const handling = line("Handling", HF, p.handlingMin);
  const customs = num(p.customs);
  const transportCost = num(p.transportCost);
  const screeningFixed = num(p.screeningFixed);

  const preFuel = freight.amount + warRisk.amount + handling.amount + customs;
  const fuelAmount = preFuel * (FS / 100);

  let baseCost = preFuel * (1 + FS / 100) + transportCost + screeningFixed;

  const profit = (baseCost * num(p.profitMarginPct)) / 100;
  const grandTotal = baseCost + profit + num(p.extra);

  /* The derivation, itemised. The operator could not reproduce £3.96/kg from
     anything on screen — no breakdown, no tooltip, and no sign that a minimum
     charge had replaced a per-kg total. For a pricing tool that is the single
     biggest trust gap, so the maths now hands back its own working. */
  const lineItems = [
    freight,
    warRisk,
    handling,
    customs ? { label: "Customs", basis: "flat", amount: customs } : null,
    FS ? { label: `Fuel surcharge (${FS}%)`, basis: "percent", amount: fuelAmount } : null,
    transportCost ? { label: "Transport", basis: "flat", amount: transportCost } : null,
    screeningFixed ? { label: "Screening", basis: "flat", amount: screeningFixed } : null,
    num(p.profitMarginPct)
      ? { label: `Margin (${num(p.profitMarginPct)}%)`, basis: "percent", amount: profit }
      : null,
    num(p.extra) ? { label: "Extra", basis: "flat", amount: num(p.extra) } : null,
  ].filter(Boolean);

  return {
    baseCost,
    grandTotal,
    // Per-kg rate is undefined when CW is 0 — never divide by zero.
    finalRate: CW > 0 ? grandTotal / CW : null,
    lineItems,
    minApplied: {
      freight: freight.minApplied,
      warRisk: warRisk.minApplied,
      handling: handling.minApplied,
    },
    anyMinApplied:
      freight.minApplied || warRisk.minApplied || handling.minApplied,
  };
}
